/**
 * SCHED-01 — turn a pending Pegasus request into a SCHEDULED job: assign a tech,
 * a day + arrival window, and (optionally) a call-ahead. This is the native
 * scheduling write-path — built on test data now, flipped live at the dispatch
 * cutover (DEC-25). ESC is never written.
 *
 * Reversible by design ([[feedback_review_before_commit]]): the modal is the
 * before/after review, and `useUnschedule` drops the job back to the queue.
 *
 * Call-ahead is per-TICKET, never pinned to the customer (Seth 2026-06-07): the
 * number defaults to the contact but the dispatcher can point it at someone else
 * for this one job.
 */
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { broadcastDataChanged } from "@/lib/boardChannel";
import { checkSubResend } from "@/features/jobs/resendPrompt";

export interface ScheduleInput {
  id: string;
  assignedEmpno: string;
  scheduledDate: string; // YYYY-MM-DD
  scheduledWindow: string;
  /** Exact arrival time "HH:MM" when the dispatcher dragged the token to a
   * precise minute; null keeps it a window-only booking (SCHED-MINIBOARD slice 2). */
  scheduledTime?: string | null;
  callAhead: boolean;
  callAheadPhone: string | null;
  /** Heads-up method on the way: a CALL or a TEXT (Seth 2026-06-24). */
  callAheadMethod?: "call" | "text";
  callAheadName?: string | null;
  /** WHO-TO-REACH (34i87): the line the confirmation text + reminder go to, who
   *  it is addressed to, and the visit's email. `undefined` = leave as-is. */
  phone?: string | null;
  contactName?: string | null;
  contactEmail?: string | null;
}

export function useScheduleDispatch() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: ScheduleInput) => {
      const { data: u } = await supabase.auth.getUser();
      const now = new Date().toISOString();
      const { error } = await supabase
        .from("pegasus_dispatch")
        .update({
          status: "scheduled",
          assigned_empno: v.assignedEmpno,
          scheduled_date: v.scheduledDate,
          scheduled_window: v.scheduledWindow,
          scheduled_time: v.scheduledTime ?? null,
          call_ahead: v.callAhead,
          call_ahead_phone: v.callAhead ? v.callAheadPhone?.trim() || null : null,
          call_ahead_method: v.callAheadMethod ?? "call",
          call_ahead_name: v.callAhead ? v.callAheadName?.trim() || null : null,
          ...(v.phone !== undefined ? { phone: v.phone?.trim() || null } : {}),
          ...(v.contactName !== undefined ? { contact_name: v.contactName?.trim() || null } : {}),
          ...(v.contactEmail !== undefined ? { contact_email: v.contactEmail?.trim() || null } : {}),
          scheduled_at: now,
          scheduled_by: u?.user?.email ?? null,
          updated_at: now,
        })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: (_r, v) => {
      // SUB-AUTO-RESEND: rescheduling a job visit the sub already has → ask.
      void checkSubResend({ dispatchId: v.id });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      // Customer-scoped 360 cards must refresh after a booking too (Seth 2026-06-24).
      qc.invalidateQueries({ queryKey: ["pending_tickets_customer"] });
      qc.invalidateQueries({ queryKey: ["scheduled_tickets_customer"] });
      qc.invalidateQueries({ queryKey: ["pegasus_dispatch"] });
      qc.invalidateQueries({ queryKey: ["scheduled_board"] });
      qc.invalidateQueries({ queryKey: ["today"] });
      // Two-monitor sync: the ambient board window picks up the new booking.
      broadcastDataChanged(["pending_tickets", "pending_tickets_customer", "scheduled_tickets_customer", "pegasus_dispatch", "scheduled_board", "today"]);
    },
  });
}

/** Undo a schedule — back to the Incoming queue, assignment cleared. */
export function useUnschedule() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const now = new Date().toISOString();
      const { error } = await supabase
        .from("pegasus_dispatch")
        .update({
          status: "pending_schedule",
          assigned_empno: null,
          scheduled_date: null,
          scheduled_window: null,
          scheduled_at: null,
          scheduled_by: null,
          updated_at: now,
        })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: (_r, id) => {
      // SUB-AUTO-RESEND: an unscheduled job visit the sub already has → ask.
      void checkSubResend({ dispatchId: id });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      // The 360's Scheduled + Pending cards key off the customer-scoped queries —
      // without these the unschedule wrote to the DB but the list never refreshed
      // (Seth 2026-06-23: "unschedule does not work"). Key mismatch = silent no-op.
      qc.invalidateQueries({ queryKey: ["pending_tickets_customer"] });
      qc.invalidateQueries({ queryKey: ["scheduled_tickets_customer"] });
      qc.invalidateQueries({ queryKey: ["scheduled_board"] });
      qc.invalidateQueries({ queryKey: ["pegasus_dispatch"] });
      qc.invalidateQueries({ queryKey: ["today"] });
      broadcastDataChanged(["pending_tickets", "pending_tickets_customer", "scheduled_tickets_customer", "scheduled_board", "pegasus_dispatch", "today"]);
    },
  });
}
